import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import type { Column } from '@tanstack/react-table';

import { Button } from '@/components/ui/button';
import { cn } from '@/shared/lib/utils';

/**
 * A sortable column header.
 *
 * Non-sortable columns render as plain text rather than a disabled button, so
 * the affordance only appears where it does something.
 *
 * The active sort indicator is the one place brand colour appears in the table
 * header — it marks the column driving the current view (§43).
 */
export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  className,
  align = 'start',
}: {
  column: Column<TData, TValue>;
  title: string;
  className?: string;
  align?: 'start' | 'end';
}) {
  if (!column.getCanSort()) {
    return <div className={cn(align === 'end' && 'text-end', className)}>{title}</div>;
  }

  const button = (
    <SortHeaderButton
      title={title}
      sorted={column.getIsSorted()}
      onToggle={() => column.toggleSorting()}
      className={className}
      align={align}
    />
  );
  // The header cell is not a flex container, so an end-aligned button needs one
  // to sit over its right-aligned values.
  return align === 'end' ? <div className="flex justify-end">{button}</div> : button;
}

/**
 * The sortable header button itself, for tables not built on TanStack Table
 * (e.g. the grouped Denominations table). Sorting stays server-side: the
 * caller only changes the requested sort.
 */
export function SortHeaderButton({
  title,
  sorted,
  onToggle,
  className,
  align = 'start',
}: {
  title: string;
  sorted: false | 'asc' | 'desc';
  onToggle: () => void;
  className?: string;
  align?: 'start' | 'end';
}) {
  const Icon = sorted === 'asc' ? ArrowUp : sorted === 'desc' ? ArrowDown : ChevronsUpDown;

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onToggle}
      className={cn(
        'h-7 gap-1 px-2 text-xs font-semibold',
        align === 'end' ? 'ms-auto -me-2' : '-ms-2',
        className,
      )}
      aria-label={`Sort by ${title}`}
    >
      <span>{title}</span>
      <Icon
        className={cn('size-3', sorted ? 'text-primary' : 'text-muted-foreground/60')}
        aria-hidden
      />
    </Button>
  );
}
